import { useEffect, useState } from 'react';
import { loadImage } from '../components/DraftPreview';
import { StationeryTemplateCategoryEnum } from '@graphql/generated';

// TODO: Make `createCanvas` in DraftPreview.tsx async, and merge together.
export async function createCanvasAsync(width: number, height: number, scale: number, draw: (ctx: CanvasRenderingContext2D) => Promise<void>) {
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);
  await draw(ctx);
  return canvas;
}

const INSITU_SIZE = 728;

function useCompsiteCanvas(
  backgroundUrl: string,
  textureUrl: string | undefined,
  pageUrl: string,
  pageDimensions: [number, number],
  foreground?: {
    url: string;
    dimensions: [number, number, number, number];
  }
) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    void (async () => {
      // Ideally in this case, the caller would skip this hook. But due to React's lack of condition hook support,
      // this is an shortcut to essentially skip this hook.
      if (backgroundUrl === '') return;

      const canvas = await createCanvasAsync(INSITU_SIZE, INSITU_SIZE, 2, async ctx => {
        const imageBackground = await loadImage(backgroundUrl, true);
        if (imageBackground) {
          ctx.drawImage(imageBackground, 0, 0, INSITU_SIZE, INSITU_SIZE);
        }

        const [w, h] = pageDimensions;
        const x = (INSITU_SIZE - w) / 2;
        const y = (INSITU_SIZE - h) / 2;

        const imageTexture = await loadImage(textureUrl, true);
        if (imageTexture) {
          ctx.drawImage(imageTexture, x, y, w, h);
        }

        const imagePage = await loadImage(pageUrl, true);
        if (imagePage) {
          ctx.save();
          ctx.globalCompositeOperation = 'multiply';
          ctx.drawImage(imagePage, x, y, w, h);
          ctx.restore();
        }

        if (foreground) {
          const imageForeground = await loadImage(foreground.url, true);
          if (imageForeground) {
            ctx.drawImage(imageForeground, ...foreground.dimensions);
          }
        }
      });
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve));
      if (!blob) return;

      const url = URL.createObjectURL(blob);
      setUrl(url);
    })();
    // The input objects and arrays are unstable,
    // but given the predictable/limited use case of this hook, it's not worth the time to stablize them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return url;
}

export function useInsituProductImage(templateCategory: string, width: number, height: number, thumbnailUrl: string) {
  const insituAssets: Record<string, Parameters<typeof useCompsiteCanvas>> = {
    [`${StationeryTemplateCategoryEnum.menu} 5 7`]: [
      'https://withjoy.com/media/raw/paper-insitu/menu-5x7-background.png',
      'https://withjoy.com/media/raw/paper-insitu/menu-tall-texture.png',
      thumbnailUrl,
      [320, 448]
    ],
    // prettier-ignore
    [`${StationeryTemplateCategoryEnum.napkin} 4.75 4.75`]: [
      'https://withjoy.com/media/raw/paper-insitu/napkin-4.75x4.75-background.png',
      undefined,
      thumbnailUrl,
      [355, 355]
    ],
    // prettier-ignore
    [`${StationeryTemplateCategoryEnum.napkin} 6.5 6.5`]: [
      'https://withjoy.com/media/raw/paper-insitu/napkin-6.5x6.5-background.png',
      undefined,
      thumbnailUrl,
      [307, 307]
    ],
    [`${StationeryTemplateCategoryEnum.sign} 18 24`]: [
      'https://withjoy.com/media/raw/paper-insitu/sign-background.jpg',
      'https://withjoy.com/media/raw/paper-insitu/sign-tall-texture.png',
      thumbnailUrl,
      [337, 450],
      {
        url: 'https://withjoy.com/media/raw/paper-insitu/sign-foreground.png',
        dimensions: [-236, 582, 1199, 486]
      }
    ],
    [`${StationeryTemplateCategoryEnum.sign} 24 18`]: [
      'https://withjoy.com/media/raw/paper-insitu/sign-background.jpg',
      'https://withjoy.com/media/raw/paper-insitu/sign-wide-texture.png',
      thumbnailUrl,
      [450, 337],
      {
        url: 'https://withjoy.com/media/raw/paper-insitu/sign-foreground.png',
        dimensions: [-236, 526, 1199, 486]
      }
    ],
    [`${StationeryTemplateCategoryEnum.sign} 24 36`]: [
      'https://withjoy.com/media/raw/paper-insitu/sign-background.jpg',
      'https://withjoy.com/media/raw/paper-insitu/sign-tall-texture.png',
      thumbnailUrl,
      [350, 525],
      {
        url: 'https://withjoy.com/media/raw/paper-insitu/sign-foreground.png',
        dimensions: [-103, 621, 934, 378]
      }
    ],
    [`${StationeryTemplateCategoryEnum.sign} 36 24`]: [
      'https://withjoy.com/media/raw/paper-insitu/sign-background.jpg',
      'https://withjoy.com/media/raw/paper-insitu/sign-wide-texture.png',
      thumbnailUrl,
      [525, 350],
      {
        url: 'https://withjoy.com/media/raw/paper-insitu/sign-foreground.png',
        dimensions: [-103, 535, 934, 378]
      }
    ]
  };

  const args = insituAssets[`${templateCategory} ${width} ${height}`] ?? ['', undefined, '', [0, 0]];
  const imageUrl = useCompsiteCanvas(...args);

  return imageUrl;
}
